import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  inject,
  signal,
} from '@angular/core';
import { buildPublicExperienceStyles } from '../../../services/global.service';
import { ProductDesignTemplatePageModuleDataContent } from '../../../store/app.model';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { LineBreakPipe } from '../../../pipes/line-break.pipe';
import { GoogleMapsModule, MapPolygon } from '@angular/google-maps';
import { GoogleMapsLoaderService } from '../../../services/google-maps-loader.service';

@Component({
  selector: 'app-text-image-video',
  imports: [CommonModule, LineBreakPipe, GoogleMapsModule, MapPolygon],
  templateUrl: './text-image-video.component.html',
})
export class TextImageVideoComponent implements OnChanges {
  // @Input({ required: true }) content!: ProductDesignTemplatePageContent;
  @Input({ required: true }) content!: any;
  @Input({ required: true }) styles!: any;
  @Input({ required: true }) isSelectedModule!: boolean;
  @Input({ required: true }) isFirstModule!: boolean;
  @Input({ required: true }) isLastModule!: boolean;

  items: ProductDesignTemplatePageModuleDataContent[] = [];
  mapsReady = signal(false);
  private mapsRequested = false;
  private readonly googleMapsLoader = inject(GoogleMapsLoaderService);
  readonly defaultMapCenter: google.maps.LatLngLiteral = { lat: 31.5837, lng: 74.4816 };
  readonly mapOptions: google.maps.MapOptions = {
    zoom: 12,
    mapTypeControl: true,
    zoomControl: true,
    streetViewControl: false,
    fullscreenControl: false,
  };
  readonly polygonMapOptions: google.maps.MapOptions = {
    mapTypeControl: false,
    mapTypeId: 'satellite',
    zoomControl: false,
    streetViewControl: false,
    fullscreenControl: false,
  };
  readonly polygonOptions: google.maps.PolygonOptions = {
    fillColor: '#2196F3',
    fillOpacity: 0.28,
    strokeColor: '#1565C0',
    strokeOpacity: 1,
    strokeWeight: 2,
    clickable: false,
    draggable: false,
    editable: false,
  };

  constructor(private sanitizer: DomSanitizer) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.content) {
      if (
        'pageModuleTemplateDataContents' in this.content &&
        Array.isArray(this.content.pageModuleTemplateDataContents)
      ) {
        this.items = this.content.pageModuleTemplateDataContents;
      } else if (
        'contentItems' in this.content &&
        Array.isArray(this.content.contentItems)
      ) {
        this.items = this.content.contentItems;
      } else if (
        'components' in this.content &&
        Array.isArray(this.content.components)
      ) {
        this.items = this.content.components;
      } else {
        this.items = [];
      }
    } else {
      this.items = [];
    }

    if (!this.mapsRequested) {
      this.mapsRequested = true;
      this.googleMapsLoader
        .loadGoogleMapsApi()
        .then(() => this.mapsReady.set(true))
        .catch(() => this.mapsReady.set(false));
    }
  }

  getSafeUrl(url: string | undefined): SafeResourceUrl {
    if (!url) {
      return '';
    }
  
    let embedUrl = url;
  
    try {
      const parsed = new URL(url);
  
      // ---------- YouTube ----------
      if (parsed.hostname.includes('youtube.com')) {
        // watch?v=VIDEO_ID
        if (parsed.pathname === '/watch') {
          const videoId = parsed.searchParams.get('v');
          if (videoId) embedUrl = `https://www.youtube.com/embed/${videoId}`;
        }
        // shorts/VIDEO_ID
        else if (parsed.pathname.startsWith('/shorts/')) {
          const videoId = parsed.pathname.split('/')[2];
          if (videoId) embedUrl = `https://www.youtube.com/embed/${videoId}`;
        }
        // playlist
        else if (parsed.pathname === '/playlist') {
          const listId = parsed.searchParams.get('list');
          if (listId) embedUrl = `https://www.youtube.com/embed/videoseries?list=${listId}`;
        }
      }
  
      // youtu.be/VIDEO_ID
      else if (parsed.hostname === 'youtu.be') {
        const videoId = parsed.pathname.slice(1);
        if (videoId) embedUrl = `https://www.youtube.com/embed/${videoId}`;
      }
  
      // ---------- Vimeo ----------
      else if (parsed.hostname.includes('vimeo.com')) {
        // vimeo.com/VIDEO_ID
        const videoId = parsed.pathname.split('/')[1];
        if (videoId) embedUrl = `https://player.vimeo.com/video/${videoId}`;
      }
    } catch {
      return '';
    }
  
    return this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);
  }

  buildStyle(type: string, style: any): string {
    return buildPublicExperienceStyles(type, style);
  }

  getLocationValue(value: any): string {
    if (!value || typeof value !== 'object') return '';
    return value.location || value.address || value.text || value.value || '';
  }

  getLocationPosition(value: any): google.maps.LatLngLiteral | null {
    const rawValue = this.getLocationValue(value);
    if (!rawValue || rawValue.trim().startsWith('@')) {
      return null;
    }
    const parts = rawValue.trim().split(/[,\s]+/);
    if (parts.length < 2) {
      return null;
    }
    const lat = Number.parseFloat(parts[0]);
    const lng = Number.parseFloat(parts[1]);
    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      return null;
    }
    return { lat, lng };
  }

  getLocationCenter(value: any): google.maps.LatLngLiteral {
    return this.getLocationPosition(value) || this.defaultMapCenter;
  }

  getLocationIframeUrl(value: any): SafeResourceUrl | null {
    const position = this.getLocationPosition(value);
    if (!position) {
      return null;
    }
    const url = `https://www.google.com/maps?q=${position.lat},${position.lng}&z=14&output=embed`;
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  getPolygonPaths(value: any): google.maps.LatLngLiteral[] {
    const raw = value?.polygon || value?.geojson || value?.value || null;
    if (!raw) {
      return [];
    }

    let coordinates: number[][][] | null = null;
    if (Array.isArray(raw)) {
      coordinates = raw as number[][][];
    } else if (typeof raw === 'string') {
      const trimmed = raw.trim();
      if (!trimmed || trimmed.startsWith('@')) {
        return [];
      }
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          coordinates = parsed as number[][][];
        } else if (parsed?.type === 'Polygon' && Array.isArray(parsed.coordinates)) {
          coordinates = parsed.coordinates as number[][][];
        } else if (
          parsed?.type === 'Feature' &&
          parsed.geometry?.type === 'Polygon' &&
          Array.isArray(parsed.geometry.coordinates)
        ) {
          coordinates = parsed.geometry.coordinates as number[][][];
        }
      } catch {
        const csvPaths = this.parseFlatPolygonString(trimmed);
        return csvPaths;
      }
    }

    if (!coordinates || !coordinates.length || !coordinates[0]?.length) {
      return [];
    }

    return coordinates[0]
      .filter((point) => Array.isArray(point) && point.length >= 2)
      .map((point) => ({ lat: Number(point[1]), lng: Number(point[0]) }))
      .filter((point) => !Number.isNaN(point.lat) && !Number.isNaN(point.lng));
  }

  private parseFlatPolygonString(value: string): google.maps.LatLngLiteral[] {
    // Supports format like: "31.7672,74.3140,31.5883,74.5626,..."
    const numbers = value
      .split(/[,\s;]+/)
      .map((part) => Number.parseFloat(part))
      .filter((num) => !Number.isNaN(num));

    if (numbers.length < 6) {
      return [];
    }

    const paths: google.maps.LatLngLiteral[] = [];
    for (let i = 0; i + 1 < numbers.length; i += 2) {
      const lat = numbers[i];
      const lng = numbers[i + 1];
      paths.push({ lat, lng });
    }

    if (paths.length < 3) {
      return [];
    }

    return paths;
  }

  getPolygonCenter(value: any): google.maps.LatLngLiteral {
    const paths = this.getPolygonPaths(value);
    if (!paths.length) {
      return this.defaultMapCenter;
    }
    let minLat = paths[0].lat;
    let maxLat = paths[0].lat;
    let minLng = paths[0].lng;
    let maxLng = paths[0].lng;

    for (const point of paths) {
      minLat = Math.min(minLat, point.lat);
      maxLat = Math.max(maxLat, point.lat);
      minLng = Math.min(minLng, point.lng);
      maxLng = Math.max(maxLng, point.lng);
    }

    return {
      lat: (minLat + maxLat) / 2,
      lng: (minLng + maxLng) / 2,
    };
  }

  fitPolygonToMap(map: google.maps.Map, value: any): void {
    const paths = this.getPolygonPaths(value);
    if (paths.length < 3) {
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    for (const point of paths) {
      bounds.extend(point);
    }
    map.fitBounds(bounds, 32);
  }
}
